import React from 'react';

interface HeartbeatAnimationProps {
  isAnimating: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  children?: React.ReactNode;
  duration?: number;
}

const HeartbeatAnimation: React.FC<HeartbeatAnimationProps> = ({
  isAnimating,
  size = 'medium',
  className = '',
  children,
  duration = 600
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8'
  };

  return (
    <>
      <style>{`
        @keyframes heartbeat {
          0% {
            transform: scale(1);
          }
          14% {
            transform: scale(1.3);
          }
          28% {
            transform: scale(1);
          }
          42% {
            transform: scale(1.3);
          }
          70% {
            transform: scale(1);
          }
        }

        @keyframes heartPulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.7;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes likeGlow {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }

        .heartbeat-animation {
          animation: ${isAnimating ? `heartbeat ${duration}ms ease-in-out` : 'none'};
        }

        .heart-pulse {
          animation: ${isAnimating ? `heartPulse ${duration}ms ease-in-out` : 'none'};
        }

        .like-glow {
          animation: ${isAnimating ? `likeGlow ${duration}ms ease-out` : 'none'};
        }
      `}</style>

      <div
        className={`
          relative inline-flex items-center justify-center
          ${sizeClasses[size]}
          ${isAnimating ? 'heartbeat-animation' : ''}
          transition-all duration-200 ease-in-out
          ${className}
        `}
      >
        {isAnimating && (
          <div
            className={`
              absolute inset-0 rounded-full
              ${sizeClasses[size]}
              like-glow
            `}
          />
        )}
        
        {isAnimating && (
          <div
            className={`
              absolute inset-0 rounded-full
              ${sizeClasses[size]}
              heart-pulse
              bg-danger-500 opacity-20
            `}
          />
        )}

        <div className={`relative z-10 ${isAnimating ? 'heart-pulse' : ''}`}>
          {children}
        </div>
      </div>
    </>
  );
};

export default HeartbeatAnimation;
